/**
 * History from the GST portal — fetch it, watch it arrive, compare it.
 * @file src/web/features/admin/components/PortalHistoryPanel.tsx
 *
 * Three parts, in the order they are used:
 *
 *   1. choose a range and what to pull, and start;
 *   2. a grid of period × return that fills in as the run goes — and says so
 *      plainly when it stops, because the usual reason is the session running
 *      out and the fix is a phone call;
 *   3. the books beside what was filed, month by month, with the differences
 *      marked.
 *
 * What is fetched is kept as its own source and never written into the books.
 * The session is the screen's one session, opened at the top: this panel reads
 * it and sends the user there, and never asks for an OTP of its own.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowUp, Check, ChevronDown, ChevronRight, CloudDownload, Loader2, Minus, Pause, Play, RefreshCw, X } from 'lucide-react';
import { adminFetchJson } from '../lib/admin-api';
import { usePeriod } from '../lib/period-context';
import { focusPortalSession, type SessionInfo } from './PortalSessionPanel';

type Kind = 'track' | 'gstr1' | 'gstr3b' | 'gstr2b' | 'gstr2a' | 'ledgers';
type ItemStatus = 'pending' | 'fetched' | 'not_filed' | 'not_available' | 'error';

interface Item { period: string; kind: Kind; status: ItemStatus; message: string | null; attempts: number }
interface Job {
  id: string; gstin: string; periods: string[]; kinds: Kind[];
  status: 'queued' | 'running' | 'paused' | 'done' | 'cancelled';
  pauseReason: string | null; message: string | null;
  createdAt: string; finishedAt: string | null; items: Item[];
}
interface StatusResp { ok: boolean; error?: string; gstin?: string; job?: Job | null }

interface Heads { taxableValue: number; igst: number; cgst: number; sgst: number; cess: number; tax: number }
interface Flag { code: string; severity: 'error' | 'warning'; message: string; amount: number }
interface CompareRow {
  period: string;
  filed: { gstr1: { arn: string | null; filedOn: string | null } | null; gstr3b: { arn: string | null; filedOn: string | null } | null } | null;
  sales: { books: Heads; gstr1: Heads | null; gstr3b: Heads | null };
  itc: { books: Heads; twoB: Heads | null; claimed: Heads | null; net: Heads | null; lateIn2b: number; runningExcess: number | null };
  documents: {
    notInGstr1: { number: string | null; party: string | null; taxableValue: number; tax: number }[];
    notInBooks: { number: string; ctin: string; taxableValue: number; tax: number }[];
    notInGstr1Count: number; notInBooksCount: number;
  } | null;
  flags: Flag[];
}
interface CompareResp { ok: boolean; error?: string; rows?: CompareRow[]; summary?: { periods: number; withFlags: number; errors: number; notFetched: number } }

const KIND_LABEL: Record<Kind, string> = {
  track: 'Filing status', gstr1: 'GSTR-1 (filed)', gstr3b: 'GSTR-3B (filed)', gstr2b: 'GSTR-2B', gstr2a: 'GSTR-2A', ledgers: 'Ledgers',
};
const KIND_HINT: Record<Kind, string> = {
  track: 'Which returns were filed, when, and the ARN',
  gstr1: 'Every table as filed, with amendments, Table 12 and 13',
  gstr3b: 'The filed summary: 3.1, 3.2, 4, 5, 5.1 and the payment',
  gstr2b: 'What suppliers declared — the credit that could be claimed',
  gstr2a: 'Optional. The running statement 2B is cut from',
  ledgers: 'Cash, credit and liability ledgers for each month',
};
const KIND_ORDER: Kind[] = ['track', 'gstr1', 'gstr3b', 'gstr2b', 'gstr2a', 'ledgers'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtPeriod = (p: string) => `${MONTHS[Number(p.slice(0, 2)) - 1] ?? p.slice(0, 2)} ${p.slice(2)}`;
const periodIndex = (p: string) => Number(p.slice(2)) * 12 + Number(p.slice(0, 2));
const inr = (n: number) => Math.round(n).toLocaleString('en-IN');
const fmtDate = (iso: string | null) => (iso ? `${iso.slice(8, 10)} ${MONTHS[Number(iso.slice(5, 7)) - 1]}` : '');
/** An error as a sentence. Anything that looks like it came out of the
 *  database layer is replaced: a query and a stack trace are not a message. */
const errText = (e: unknown) => {
  const text = e instanceof Error ? e.message.replace(/^\d+\s/, '') : '';
  if (!text) return 'Something went wrong. Try again.';
  if (/prisma|invocation|\bP\d{4}\b|\n\s+at |does not exist in the current database/i.test(text) || text.length > 300) {
    return 'Something went wrong on the server while loading portal history. Try again; if it keeps happening, the server log has the details.';
  }
  return text;
};
const lastDay = (ym: string) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m, 0).getDate(); };

/** The financial year a date falls in, by its starting year (April–March). */
const fyOf = (d: Date) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);
const fyLabel = (y: number) => `${y}-${String((y + 1) % 100).padStart(2, '0')}`;

const STATUS_STYLE: Record<ItemStatus, { cls: string; label: string }> = {
  pending: { cls: 'border-ink-200 bg-paper text-ink-400', label: 'Pending' },
  fetched: { cls: 'border-ok-200 bg-ok-50 text-ok-700', label: 'Fetched' },
  not_filed: { cls: 'border-ink-200 bg-ink-50 text-ink-500', label: 'Not filed' },
  not_available: { cls: 'border-ink-200 bg-ink-50 text-ink-500', label: 'Not available' },
  error: { cls: 'border-danger-200 bg-danger-50 text-danger-700', label: 'Error' },
};

export function PortalHistoryPanel({ entityId, gstRegistrationId, session }: {
  entityId: string | null; gstRegistrationId?: string | null; session: SessionInfo | null;
}) {
  const q = useMemo(() => ({ entityId: entityId ?? undefined, gstRegistrationId: gstRegistrationId ?? undefined }), [entityId, gstRegistrationId]);
  const live = !!session?.session;

  /* ── what to fetch ── */
  const thisFy = fyOf(new Date());
  const fyChoices = [thisFy, thisFy - 1, thisFy - 2, thisFy - 3, thisFy - 4];
  const [years, setYears] = useState<number[]>([thisFy - 1]);
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [kinds, setKinds] = useState<Record<Kind, boolean>>({ track: true, gstr1: true, gstr3b: true, gstr2b: true, gstr2a: false, ledgers: false });
  const range = useMemo(() => {
    // The real last day: this range also bounds the books side of the
    // comparison, and a month cut short would drop its last invoices.
    if (custom) return { from: custom.from ? `${custom.from}-01` : '', to: custom.to ? `${custom.to}-${String(lastDay(custom.to)).padStart(2, '0')}` : '' };
    if (!years.length) return { from: '', to: '' };
    return { from: `${Math.min(...years)}-04-01`, to: `${Math.max(...years) + 1}-03-31` };
  }, [years, custom]);
  const toggleYear = (y: number) => { setCustom(null); setYears((ys) => (ys.includes(y) ? ys.filter((x) => x !== y) : [...ys, y])); };
  // Chosen years are fetched as one span, so the ones in between come too.
  const span = years.length ? `FY ${fyLabel(Math.min(...years))}${years.length > 1 ? ` to ${fyLabel(Math.max(...years))}` : ''}` : '';
  const gap = years.length > 1 && Math.max(...years) - Math.min(...years) + 1 > years.length;

  /* ── the run ── */
  const [status, setStatus] = useState<StatusResp | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const job = status?.job ?? null;
  const running = job?.status === 'running' || job?.status === 'queued';

  const load = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<StatusResp>('/api/gst/portal/history', { query: q })
      .then(setStatus)
      .catch((e) => setStatus({ ok: false, error: errText(e) }));
  }, [entityId, q]);
  useEffect(() => { setStatus(null); load(); }, [load]);

  /* Open on the range that was last fetched, not on a default. Someone coming
     back to a finished fetch wants to see THAT fetch compared — once, on first
     load, so it never fights a choice they then make. */
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !job?.periods.length) return;
    seeded.current = true;
    const sorted = [...job.periods].sort((a, b) => periodIndex(a) - periodIndex(b));
    const first = sorted[0], last = sorted[sorted.length - 1];
    const fy = (pd: string) => (Number(pd.slice(0, 2)) >= 4 ? Number(pd.slice(2)) : Number(pd.slice(2)) - 1);
    const ym = (pd: string) => `${pd.slice(2)}-${pd.slice(0, 2)}`;
    const now = new Date();
    // Whole financial years (the current one runs to this month, not to March).
    const wholeYears = first.slice(0, 2) === '04' && (last.slice(0, 2) === '03' || ym(last) === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    if (wholeYears) { setCustom(null); setYears(Array.from({ length: fy(last) - fy(first) + 1 }, (_, i) => fy(first) + i)); }
    else setCustom({ from: ym(first), to: ym(last) });
  }, [job]);

  // Poll only while something is moving. A finished grid does not change.
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(load, 3000);
    return () => window.clearInterval(t);
  }, [running, load]);

  async function act(name: string, fn: () => Promise<any>) {
    setBusy(name); setMsg(null);
    try { const r = await fn(); load(); return r; }
    catch (e) { setMsg({ tone: 'err', text: errText(e) }); return null; }
    finally { setBusy(null); }
  }
  const start = () => act('start', () => adminFetchJson('/api/gst/portal/history', {
    method: 'POST', query: q,
    body: JSON.stringify({ from: range.from, to: range.to, kinds: KIND_ORDER.filter((k) => kinds[k]) }),
  }));
  const resume = () => job && act('resume', () => adminFetchJson(`/api/gst/portal/history/${job.id}/resume`, { method: 'POST', query: q }));
  const cancel = () => job && act('cancel', () => adminFetchJson(`/api/gst/portal/history/${job.id}/cancel`, { method: 'POST', query: q }));

  /* ── the comparison ──
     Follows the period chosen for the fetch above. It used to follow only the
     financial year in the top bar, so fetching FY 2024-25 while the bar said
     2026-27 showed a table of months nothing had been fetched for. The top
     bar's year is the fallback, for when no period is chosen here. */
  const p = usePeriod();
  const compareRange = range.from && range.to
    ? range
    : p.startYear != null ? { from: `${p.startYear}-04-01`, to: `${p.startYear + 1}-03-31` } : null;
  const compareLabel = custom
    ? (custom.from && custom.to ? `${fmtPeriod(custom.from.slice(5, 7) + custom.from.slice(0, 4))} – ${fmtPeriod(custom.to.slice(5, 7) + custom.to.slice(0, 4))}` : '')
    : span || (p.startYear != null ? `FY ${fyLabel(p.startYear)}` : '');
  const [compare, setCompare] = useState<CompareResp | null>(null);
  const [comparing, setComparing] = useState(false);
  const loadCompare = useCallback(() => {
    if (!entityId || !compareRange) return;
    setComparing(true);
    adminFetchJson<CompareResp>('/api/gst/portal/compare', { query: { ...q, from: compareRange.from, to: compareRange.to } })
      .then(setCompare)
      .catch((e) => setCompare({ ok: false, error: errText(e) }))
      .finally(() => setComparing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, q, compareRange?.from, compareRange?.to]);
  useEffect(() => { setCompare(null); loadCompare(); }, [loadCompare]);
  // A run that has just finished changed what there is to compare.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) loadCompare();
    wasRunning.current = running;
  }, [running, loadCompare]);

  const anyKind = KIND_ORDER.some((k) => kinds[k]);
  const canStart = live && anyKind && !!range.from && !!range.to && !running && !busy;

  return (
    <div className="space-y-6">
      {/* ── 1. fetch ── */}
      <div className="rounded-2xl border border-ink-200 bg-paper p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900"><CloudDownload className="h-4 w-4 text-ink-400" /> Fetch history from the GST portal</h3>
            <p className="mt-0.5 max-w-2xl text-xs text-ink-500">
              Pulls what GSTN already holds for past periods. It is kept as its own source — “filed on portal” — beside the books, and never changes a voucher. Nothing is filed or altered on the portal.
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-5 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Period</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {fyChoices.map((y) => (
                <button key={y} onClick={() => toggleYear(y)}
                  className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${!custom && years.includes(y) ? 'border-inkblack bg-inkblack text-white' : 'border-ink-300 bg-paper text-ink-700 hover:bg-ink-50'}`}>
                  FY {fyLabel(y)}
                </button>
              ))}
              <button onClick={() => setCustom(custom ? null : { from: `${thisFy}-04`, to: new Date().toISOString().slice(0, 7) })}
                className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${custom ? 'border-inkblack bg-inkblack text-white' : 'border-ink-300 bg-paper text-ink-700 hover:bg-ink-50'}`}>
                Months…
              </button>
            </div>
            {custom ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-600">
                <input type="month" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="rounded-lg border border-ink-300 px-2 py-1 text-xs" aria-label="From month" />
                <span>to</span>
                <input type="month" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="rounded-lg border border-ink-300 px-2 py-1 text-xs" aria-label="To month" />
              </div>
            ) : (
              <p className="mt-2 text-[11px] text-ink-500">
                {span || 'Choose at least one year.'}{gap && ' — the years in between are fetched too.'}
              </p>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">What to fetch</p>
            <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
              {KIND_ORDER.map((k) => (
                <label key={k} className="flex cursor-pointer items-start gap-2 rounded-lg border border-ink-200 px-2.5 py-1.5 hover:bg-ink-50">
                  <input type="checkbox" checked={kinds[k]} onChange={(e) => setKinds((s) => ({ ...s, [k]: e.target.checked }))} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-ink-800">{KIND_LABEL[k]}</span>
                    <span className="block text-[11px] leading-snug text-ink-400">{KIND_HINT[k]}</span>
                  </span>
                </label>
              ))}
            </div>
            {(kinds.gstr1 || kinds.gstr3b) && !kinds.track && (
              <p className="mt-1.5 text-[11px] text-ink-500">Filing status is read first either way — it decides which months have a filed return to fetch.</p>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={start} disabled={!canStart}
            className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-white disabled:opacity-40">
            {busy === 'start' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />} Fetch history
          </button>
          {!live && (
            <span className="flex flex-wrap items-center gap-2 text-[11px] text-warn-700">
              No portal session — one code from the client covers the whole fetch.
              <button onClick={focusPortalSession} className="inline-flex items-center gap-1 rounded-lg border border-warn-300 bg-paper px-2 py-0.5 font-semibold text-warn-800 hover:bg-warn-50">
                <ArrowUp className="h-3 w-3" /> Open a session
              </button>
            </span>
          )}
          {running && <span className="text-[11px] text-ink-500">A fetch is running — it carries on if you leave this page.</span>}
          {msg && <span className={`text-[11px] ${msg.tone === 'ok' ? 'text-ok-700' : 'text-danger-700'}`}>{msg.text}</span>}
        </div>
      </div>

      {/* ── 2. progress ── */}
      {status && !status.ok && status.error && (
        <div className="rounded-2xl border border-danger-200 bg-danger-50 px-5 py-3 text-xs text-danger-700">{status.error}</div>
      )}
      {job && <JobProgress job={job} live={live} busy={busy} onResume={resume} onCancel={cancel} onRefresh={load} />}

      {/* ── 3. comparison ── */}
      <div className="rounded-2xl border border-ink-200 bg-paper">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-900">Books against what was filed{compareLabel ? ` — ${compareLabel}` : ''}</h3>
            <p className="text-xs text-ink-500">For the period chosen above. Each figure from its own source; a dash means that source has not been fetched for the month — not that it was nil.</p>
          </div>
          <button onClick={loadCompare} disabled={comparing} className="inline-flex items-center gap-1.5 rounded-lg border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
            {comparing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
          </button>
        </div>
        {!compare && <p className="px-5 py-4 text-sm text-ink-500">{compareRange ? 'Comparing…' : 'Choose a period above to compare.'}</p>}
        {compare && !compare.ok && <p className="px-5 py-4 text-sm text-danger-700">{compare.error}</p>}
        {compare?.ok && compare.rows && <CompareTable rows={compare.rows} summary={compare.summary} />}
      </div>
    </div>
  );
}

/* ── progress grid ──────────────────────────────────────────────────────── */

function JobProgress({ job, live, busy, onResume, onCancel, onRefresh }: {
  job: Job; live: boolean; busy: string | null; onResume: () => void; onCancel: () => void; onRefresh: () => void;
}) {
  const running = job.status === 'running' || job.status === 'queued';
  const done = job.items.filter((i) => i.status !== 'pending').length;
  const errors = job.items.filter((i) => i.status === 'error');
  const byKey = new Map(job.items.map((i) => [`${i.period}|${i.kind}`, i]));
  const kinds = KIND_ORDER.filter((k) => job.kinds.includes(k));
  const periods = [...job.periods].sort((a, b) => periodIndex(b) - periodIndex(a));
  const needsOtp = job.status === 'paused' && job.pauseReason === 'session_expired';

  return (
    <div className="rounded-2xl border border-ink-200 bg-paper">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-5 py-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            {running && <Loader2 className="h-4 w-4 animate-spin text-ink-400" />}
            {job.status === 'paused' && <Pause className="h-4 w-4 text-warn-600" />}
            {job.status === 'done' && <Check className="h-4 w-4 text-ok-600" />}
            {job.status === 'cancelled' && <X className="h-4 w-4 text-ink-400" />}
            {running ? 'Fetching' : job.status === 'paused' ? 'Paused' : job.status === 'done' ? 'Fetched' : 'Cancelled'}
            <span className="font-normal text-ink-500">· {done} of {job.items.length}</span>
          </h3>
          <p className="truncate text-xs text-ink-500">
            GSTIN {job.gstin} · {fmtPeriod(job.periods[0])}{job.periods.length > 1 ? ` – ${fmtPeriod(job.periods[job.periods.length - 1])}` : ''}
            {job.message && job.status !== 'paused' ? ` · ${job.message}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(job.status === 'paused' || (job.status === 'done' && errors.length > 0)) && (
            <button onClick={onResume} disabled={!!busy || !live} title={live ? undefined : 'Open a portal session first'}
              className="inline-flex items-center gap-1.5 rounded-lg bg-inkblack px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-40">
              {busy === 'resume' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              {job.status === 'paused' ? 'Resume' : 'Retry the errors'}
            </button>
          )}
          {(running || job.status === 'paused') && (
            <button onClick={onCancel} disabled={!!busy} className="rounded-lg border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">Cancel</button>
          )}
          {!running && <button onClick={onRefresh} className="rounded-lg p-1 text-ink-400 hover:text-ink-700" aria-label="Refresh"><RefreshCw className="h-3.5 w-3.5" /></button>}
        </div>
      </div>

      {job.status === 'paused' && (
        <div className="flex flex-wrap items-center gap-2 border-b border-warn-200 bg-warn-50 px-5 py-2.5 text-xs text-warn-800">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>{needsOtp ? 'Session expired — request a new OTP. The fetch resumes from where it stopped.' : job.message ?? 'Paused.'}</span>
          {needsOtp && !live && (
            <button onClick={focusPortalSession} className="inline-flex items-center gap-1 rounded-lg border border-warn-300 bg-paper px-2 py-0.5 text-[11px] font-semibold hover:bg-warn-50">
              <ArrowUp className="h-3 w-3" /> Request new OTP
            </button>
          )}
          {needsOtp && live && <span className="font-semibold">Session open — press Resume.</span>}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-ink-100 text-left text-[11px] uppercase tracking-wide text-ink-400">
              <th className="px-5 py-2 font-medium">Period</th>
              {kinds.map((k) => <th key={k} className="px-2 py-2 font-medium">{KIND_LABEL[k].replace(' (filed)', '')}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {periods.map((period) => (
              <tr key={period}>
                <td className="whitespace-nowrap px-5 py-1.5 font-medium text-ink-800">{fmtPeriod(period)}</td>
                {kinds.map((k) => {
                  const it = byKey.get(`${period}|${k}`);
                  if (!it) return <td key={k} className="px-2 py-1.5 text-ink-300"><Minus className="h-3 w-3" /></td>;
                  const s = STATUS_STYLE[it.status];
                  return (
                    <td key={k} className="px-2 py-1.5">
                      <span title={it.message ?? undefined} className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>{s.label}</span>
                      {it.message && it.status === 'fetched' && <span className="ml-1.5 hidden text-[11px] text-ink-400 xl:inline">{it.message}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {errors.length > 0 && (
        <div className="border-t border-ink-100 px-5 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-danger-700">Could not be fetched</p>
          <ul className="mt-1.5 space-y-1">
            {errors.map((e) => (
              <li key={`${e.period}|${e.kind}`} className="text-[11px] text-ink-600">
                <span className="font-medium text-ink-800">{fmtPeriod(e.period)} · {KIND_LABEL[e.kind]}</span> — {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── comparison table ───────────────────────────────────────────────────── */

/** A filed figure beside the one it should agree with. Red when it does not. */
function Fig({ value, against }: { value: number | null | undefined; against?: number | null }) {
  if (value == null) return <span className="text-ink-300">—</span>;
  const off = against != null && Math.abs(value - against) > 1;
  return <span className={off ? 'rounded bg-danger-50 px-1 font-semibold text-danger-700' : 'text-ink-700'}>{inr(value)}</span>;
}

function CompareTable({ rows, summary }: { rows: CompareRow[]; summary?: CompareResp['summary'] }) {
  const [open, setOpen] = useState<string | null>(null);
  const th = 'px-2 py-2 text-right font-medium';
  const td = 'px-2 py-1.5 text-right tabular-nums';
  return (
    <>
      {summary && (
        <p className="border-b border-ink-100 px-5 py-2 text-[11px] text-ink-500">
          {summary.withFlags === 0 ? 'No differences found in what has been fetched.' : `${summary.withFlags} of ${summary.periods} months have differences.`}
          {summary.notFetched > 0 && ` ${summary.notFetched} month${summary.notFetched === 1 ? ' has' : 's have'} nothing fetched from the portal yet.`}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-ink-100 text-[11px] uppercase tracking-wide text-ink-400">
              <th className="px-5 py-2 text-left font-medium" rowSpan={2}>Period</th>
              <th className="border-l border-ink-100 px-2 py-1.5 text-center font-medium" colSpan={3}>Taxable sales</th>
              <th className="border-l border-ink-100 px-2 py-1.5 text-center font-medium" colSpan={3}>Output tax</th>
              <th className="border-l border-ink-100 px-2 py-1.5 text-center font-medium" colSpan={3}>Input credit</th>
              <th className="border-l border-ink-100 px-3 py-2 text-left font-medium" rowSpan={2}>Differences</th>
            </tr>
            <tr className="border-b border-ink-100 text-[11px] text-ink-400">
              <th className={`${th} border-l border-ink-100`}>Books</th><th className={th}>GSTR-1</th><th className={th}>3B</th>
              <th className={`${th} border-l border-ink-100`}>Books</th><th className={th}>GSTR-1</th><th className={th}>3B</th>
              <th className={`${th} border-l border-ink-100`}>Books</th><th className={th}>2B</th><th className={th}>3B claimed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {rows.map((r) => {
              const isOpen = open === r.period;
              const errors = r.flags.filter((f) => f.severity === 'error').length;
              return (
                <Fragment key={r.period}>
                  <tr className={r.flags.length ? 'cursor-pointer hover:bg-ink-50/60' : ''} onClick={() => r.flags.length && setOpen(isOpen ? null : r.period)}>
                    <td className="whitespace-nowrap px-5 py-1.5">
                      <span className="font-medium text-ink-800">{fmtPeriod(r.period)}</span>
                      <span className="block text-[11px] text-ink-400">
                        {!r.filed ? 'status not fetched'
                          : [r.filed.gstr1 ? `R1 ${fmtDate(r.filed.gstr1.filedOn)}` : 'R1 not filed', r.filed.gstr3b ? `3B ${fmtDate(r.filed.gstr3b.filedOn)}` : '3B not filed'].join(' · ')}
                      </span>
                    </td>
                    <td className={`${td} border-l border-ink-100 text-ink-700`}>{inr(r.sales.books.taxableValue)}</td>
                    <td className={td}><Fig value={r.sales.gstr1?.taxableValue} against={r.sales.books.taxableValue} /></td>
                    <td className={td}><Fig value={r.sales.gstr3b?.taxableValue} against={r.sales.gstr1?.taxableValue ?? r.sales.books.taxableValue} /></td>
                    <td className={`${td} border-l border-ink-100 text-ink-700`}>{inr(r.sales.books.tax)}</td>
                    <td className={td}><Fig value={r.sales.gstr1?.tax} against={r.sales.books.tax} /></td>
                    <td className={td}><Fig value={r.sales.gstr3b?.tax} against={r.sales.gstr1?.tax ?? r.sales.books.tax} /></td>
                    <td className={`${td} border-l border-ink-100 text-ink-700`}>{inr(r.itc.books.tax)}</td>
                    <td className={td}><Fig value={r.itc.twoB?.tax} /></td>
                    <td className={td}><Fig value={r.itc.claimed?.tax} against={r.itc.twoB?.tax} /></td>
                    <td className="border-l border-ink-100 px-3 py-1.5">
                      {r.flags.length === 0 ? <span className="text-ink-300">—</span> : (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${errors ? 'bg-danger-50 text-danger-700' : 'bg-warn-50 text-warn-700'}`}>
                          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} {r.flags.length}
                        </span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={11} className="bg-ink-50/50 px-5 py-3">
                        <ul className="space-y-1">
                          {r.flags.map((f) => (
                            <li key={f.code} className={`flex items-start gap-1.5 text-xs ${f.severity === 'error' ? 'text-danger-700' : 'text-warn-700'}`}>
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {f.message}
                            </li>
                          ))}
                        </ul>
                        {r.documents && (r.documents.notInGstr1Count > 0 || r.documents.notInBooksCount > 0) && (
                          <div className="mt-3 grid gap-4 md:grid-cols-2">
                            {r.documents.notInGstr1Count > 0 && (
                              <DocList title="In the books, not in the filed GSTR-1" total={r.documents.notInGstr1Count}
                                rows={r.documents.notInGstr1.map((d) => ({ a: d.number ?? '—', b: d.party ?? '', taxable: d.taxableValue, tax: d.tax }))} />
                            )}
                            {r.documents.notInBooksCount > 0 && (
                              <DocList title="In the filed GSTR-1, not in the books" total={r.documents.notInBooksCount}
                                rows={r.documents.notInBooks.map((d) => ({ a: d.number, b: d.ctin, taxable: d.taxableValue, tax: d.tax }))} />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DocList({ title, total, rows }: { title: string; total: number; rows: { a: string; b: string; taxable: number; tax: number }[] }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">{title} · {total}</p>
      <table className="mt-1 w-full text-[11px]">
        <tbody>
          {rows.map((d, i) => (
            <tr key={i} className="border-t border-ink-100">
              <td className="py-1 pr-2 font-medium text-ink-800">{d.a}</td>
              <td className="py-1 pr-2 text-ink-500">{d.b}</td>
              <td className="py-1 pr-2 text-right tabular-nums text-ink-700">{inr(d.taxable)}</td>
              <td className="py-1 text-right tabular-nums text-ink-700">{inr(d.tax)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > rows.length && <p className="mt-1 text-[11px] text-ink-400">and {total - rows.length} more</p>}
    </div>
  );
}
